import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { createLoadOptions, useDebounce } from "../../../../../hooks/useDebounce";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { FasteningChoice } from "../../../../../http/border/types/fasteningChoices";
import { useFasteningChoicesApi } from "../../../../../http/border/useFasteningChoices";
import { useSubstructuresApi } from "../../../../../http/border/useSubstructures";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateFasteningChoiceModalDialogButton from "./CreateFasteningChoiceModalDialogButton";
import EditFasteningChoice from "./EditFasteningChoice";

const getCombinationLabel = (fasteningChoice: FasteningChoice) =>
  fasteningChoice.fastenings.map((fastening) => fastening.item.code).join(" + ") || "-";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const FasteningChoices: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const { borderSystemId } = useParams<{ borderSystemId: string }>();
  const filters = useListFilters({ key: "fasteningChoices" });
  const aborter = useRef(new AbortController());
  const fasteningChoicesApi = useFasteningChoicesApi();
  const substructuresApi = useSubstructuresApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "fasteningChoices");

  const isActiveTab = active ?? true;

  const substructureSearchTerm = useRef("");
  const substructureAborter = useRef(new AbortController());
  const loadSubstructureOptions = createLoadOptions(
    substructureAborter,
    substructureSearchTerm,
    substructuresApi.getAdminSubstructures,
  );

  useEffect(() => {
    loadSubstructureOptions(substructureSearchTerm.current);
  }, []);

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await fasteningChoicesApi.updateAdminFasteningChoiceVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await fasteningChoicesApi.updateAdminFasteningChoiceOrder(id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToEdit, setItemToEdit] = useState<FasteningChoice>();
  const [itemToDelete, setItemToDelete] = useState<FasteningChoice>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);
  const query = {
    ...filters.state,
    q: debouncedValue,
    systemId: borderSystemId,
  };

  const { error, data } = fasteningChoicesApi.useAdminFasteningChoices(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.fasteningChoices.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.fasteningChoices.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateFasteningChoiceModalDialogButton systemId={borderSystemId!} />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<FasteningChoice>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th align="left">{i18n.translation.substructures.singular}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>{getCombinationLabel(item)}</td>
            <td>{item.substructure.name}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.fasteningChoices.edit}>
          <EditFasteningChoice
            onClose={() => setItemToEdit(undefined)}
            fasteningChoiceId={itemToEdit.id}
            systemId={borderSystemId!}
          />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.fasteningChoices.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                fasteningChoicesApi
                  .deleteAdminFasteningChoice(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.fasteningChoices.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default FasteningChoices;
